import { useEffect } from "react";

import { hardLogout } from "@/lib/session";
import { isTokenLive, useAuthStore } from "@/store/use-auth-store";

/** Token muddati tugashidan qancha oldin yangilanadi. */
export const REFRESH_AHEAD_MS = 60_000;

/** Juda kichik oraliqda qayta-qayta urinmaslik uchun pastki chegara. */
const MIN_DELAY_MS = 5_000;

/**
 * Keyingi yangilashgacha kutish (ms). Token allaqachon eskirgan yoki
 * tugashiga bir daqiqadan kam qolgan bo'lsa — darhol (0).
 */
export const msUntilRefresh = (expSeconds: number, now = Date.now()): number => {
  const due = expSeconds * 1000 - REFRESH_AHEAD_MS - now;
  if (due <= 0) return 0;
  return Math.max(due, MIN_DELAY_MS);
};

/**
 * `storage` hodisasidagi yangi qiymatdan "kirgan" bayrog'ini o'qiydi.
 * Buzilgan yoki begona qiymat — "kirmagan" deb hisoblanadi.
 */
export const readPersistedAuthFlag = (raw: string | null): boolean => {
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { state?: { auth?: unknown } } | null;
    return parsed?.state?.auth === true;
  } catch {
    return false;
  }
};

/**
 * Access token'ni muddati tugashidan OLDIN yangilab turadi.
 *
 * Ilgari token 15 daqiqadan keyin jimgina eskirardi: keyingi render'da
 * guard uni "kirmagan" deb bilib login'ga uloqtirardi, so'rovlar esa
 * 401 bo'ronini boshlardi. Endi token o'z vaqtida almashadi, uzoq
 * uxlagan tab esa ko'ringan zahoti yangilanadi.
 */
export const useSessionKeepAlive = () => {
  const auth = useAuthStore(state => state.auth);
  const exp = useAuthStore(state => state.decodedToken?.exp);

  useEffect(() => {
    if (!auth || !exp) return;
    const timer = setTimeout(
      () => void useAuthStore.getState().refreshSession(),
      msUntilRefresh(exp),
    );
    return () => clearTimeout(timer);
  }, [auth, exp]);

  useEffect(() => {
    // Uxlagan noutbuk yoki fonda qolgan tab: taymer o'z vaqtida ishlamagan
    // bo'lishi mumkin — tab ko'ringan zahoti tekshiramiz.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const state = useAuthStore.getState();
      if (!state.auth || state.rehydrating) return;
      const liveFor = (state.decodedToken?.exp ?? 0) * 1000 - Date.now();
      if (!isTokenLive(state.decodedToken) || liveFor < REFRESH_AHEAD_MS) {
        void state.refreshSession();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
};

/**
 * Tablar orasida kirish/chiqishni moslaydi.
 *
 * Bitta tabda chiqilsa, qolganlari eski access token bilan 15 daqiqagacha
 * ishlab yurardi; boshqa tabda kirilsa esa bu tab "kirmagan" bo'lib
 * qolaverardi. `persist` har o'zgarishni localStorage'ga yozadi va brauzer
 * uni boshqa tablarga `storage` hodisasi bilan yuboradi. Faqat bayroq
 * O'ZGARGANDA ish qilinadi — aks holda ikki tab bir-birini cheksiz
 * yangilab yotardi.
 */
export const useCrossTabAuthSync = () => {
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== "auth") return;
      const next = readPersistedAuthFlag(event.newValue);
      const state = useAuthStore.getState();
      if (next === state.auth) return;
      if (next) void state.refreshSession();
      else hardLogout();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
};
